/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from "react";
import { findIndex, isEqual, sumBy } from "lodash";
import classNames from "classnames";

import inputConnect from "./inputConnect";
import BudgetLineInput from "./BudgetLineInput";
import { useForm } from "../Form/withFormStore";

const BudgetChapterInput = props => {
  const { values, updateValue, fieldName, budgetLines, unit } = props;
  const { formKey } = useForm();

  const budgetLineIndexFor = useCallback(({ id }) => findIndex(values, { id }), [values]);

  const isChecked = useCallback(
    budgetLine => budgetLineIndexFor(budgetLine) >= 0,
    [budgetLineIndexFor]
  );

  const valuesTotal = useCallback(vals => sumBy(vals, ({ value }) => value), []);

  const handleOptionChanged = useCallback(
    ({ id, checked, value, optionIds }) => {
      const newValues = [...values];
      const budgetLineIndex = budgetLineIndexFor({ id });
      const wasChecked = budgetLineIndex >= 0;

      if (checked && !wasChecked) {
        // Add the budget line to the values if it was just checked
        newValues.push({ id, value, optionIds });
      } else if (!checked && wasChecked) {
        // Remove the budget line from the values if it was unchecked
        newValues.splice(budgetLineIndex, 1);
      } else if (checked && wasChecked) {
        const newValue = { id, value, optionIds };
        // Don't update anything if the checked state and value didn't change
        if (isEqual(values[budgetLineIndex], newValue)) return;
        // Update the value if it changed since the previous one. This may happen when
        // the user modifies the value from the slider of an already selected budget line.
        newValues[budgetLineIndex] = newValue;
      }

      // Calculate the new total of the budget chapter
      const total = valuesTotal(newValues);
      // Update the values and total
      updateValue({ values: newValues, total });
    },
    [budgetLineIndexFor, isChecked, updateValue, values, valuesTotal]
  );

  const currentValueFor = useCallback(
    budgetLine => {
      const selectedLine = values.find(v => v.id === budgetLine.id);
      return budgetLine.withOptions ? selectedLine?.value ?? 0 : budgetLine.value;
    },
    [values]
  );

  const currentOptionIdsFor = useCallback(
    budgetLine => values.find(v => v.id === budgetLine.id)?.optionIds || [],
    [values]
  );

  // Update the `total` value on mount
  useEffect(() => {
    const total = valuesTotal(values);

    updateValue({ values, total });
  }, [valuesTotal, formKey]);

  const className = classNames("budget-chapter", props.className);

  return (
    <div className={className}>
      {budgetLines.map(budgetLine => (
        <BudgetLineInput
          key={budgetLine.id}
          budgetLine={budgetLine}
          fieldName={fieldName}
          isLineChecked={isChecked(budgetLine)}
          currentValue={currentValueFor(budgetLine)}
          currentOptionIds={currentOptionIdsFor(budgetLine)}
          unit={unit}
          onUpdate={handleOptionChanged}
        />
      ))}
    </div>
  );
};

export default inputConnect(BudgetChapterInput, {
  fields: { values: [], total: 0 }
});
